import React, { useCallback, useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import * as actions from "../../actionCreator";
import * as settingsActions from "modules/settings/actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";
import moment from "moment";
import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";
import Loading from "modules/common/components/loading";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import BasePage from "pages/common/basePage";
import Breadcrumb from "pages/common/breadcrumb";
import MainContent from "pages/common/mainContent";
import confirm from "utils/notyPopups";
import { Event, Reminder, ValidationChecks } from "../../models";
import EventPreview from "../event-views/eventPreview";
import EventEditor from "./eventEditor";
import Validation from "./validation";
import { PickerLocalization } from "modules/common/components/pickerLocalization";
import { ImageScale } from "modules/posts/models";
import { submissionsApi } from "api/instances";
import { useCustomTinyMceCssContent } from "modules/common/hooks/data/useCustomTinyMceCssContent";
import ContentGuidelinePreview from "modules/settings/components/content/guidelines/contentGuidelinePreview";
import EventAuthoringActionsAppBar from "./components/eventAuthoringActionsAppBar";
import { HintType } from "modules/common/components/authoring/dialogs/guidelineHint";
import { getHighestRole } from "utils/userRoleUtils";
import { UserRoleStrings } from "modules/authorization/models";
import RejectSubmissionDialog from "modules/common/components/authoring/dialogs/rejectSubmissionDialog";
import DiscardDraftConfirmDialog from "modules/common/components/authoring/dialogs/discardDraftConfirmDialog";
import DraftHistoryButton from "modules/common/components/authoring/draftLog/draftHistoryButton";
import { useDefaultLocale } from "modules/common/hooks/useDefaultLocale";
import { useOpenContentGuidelines } from "modules/common/hooks/useOpenContentGuidelines";
import OneLastCheck from "modules/common/components/authoring/dialogs/oneLastCheck";
import { getPublishBtnName } from "modules/common/components/authoring/utils";
import EventPreviewContent from "../event-views/eventPreviewContent";
import { ContentType } from "modules/common/models";
import { defaultActionButtons } from "modules/common/models";
import { getDefaultNotificationSetting, isAnyTenantEventRemindersEnabled } from "utils/notificationsHelper";
import { NotificationEvents } from "modules/messaging";

import "modules/common/components/authoring/authoring.sass";
import "./eventCreation.sass";

const EventCreation: React.FunctionComponent<PropsWithRedux> = ({
    currentUser,
    tenantSettings,
    notificationSettings,
    tagSettings,
    changedSinceSaved,
    isUpdatingUrl,
    isSaving,
    errorMessage,
    match,
    tenantId,
    isPublishing,
    clearErrorMessage,
    hasChangedSinceSaved,
    getDraft,
    clearChangedSinceSaved,
    updateUrlComplete,
    getTagSettings,
    publishEvent,
    submitEvent,
    saveEvent,
    redirectTo,
    updateUrl
}) => {
    const newEvent = React.useMemo((): Event => {
        const author: string = currentUser
            ? (currentUser.preferredName
                ? `${currentUser.preferredName} ${currentUser.lastName}`
                : `${currentUser.firstName} ${currentUser.lastName}`)
            : "";
        const authorEmail: string = currentUser ? currentUser.email : "";
        const bannerColor: string = (tenantSettings && tenantSettings.themeSettings && tenantSettings.themeSettings.color) || "#2196f3";
        const lcid: string = tenantSettings ? tenantSettings.defaultLCID : "en-us";
        const eventStartTime: string = moment(new Date()).startOf("hour").add(1, "hour").add(1, "day").toISOString();
        const eventEndTime: string = moment(eventStartTime).add(30, "minutes").toISOString();

        const newEvent: Event = {
            id: "",
            attachedContent: [],
            author: author,
            authorEmail: authorEmail,
            bannerColor: bannerColor,
            capacity: {
                InPerson: -1,
                NotAttending: -1,
                Online: -1,
                Waitlist: -1
            },
            commentingEnabled: false,
            eventTimes: {
                [eventStartTime]: eventEndTime
            },
            eventType: "informational",
            fileAttachments: [],
            locationLink: "",
            locationLinkLabel: "",
            notifications: {
                emailOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedEmail, notificationSettings),
                mobileOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedMobile, notificationSettings),
                reminders: isAnyTenantEventRemindersEnabled(notificationSettings) ?
                    notificationSettings.defaultEventSettings?.reminders as Reminder[] || [] :
                    [],
                smsOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedSMS, notificationSettings),
                teamsOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedTeams, notificationSettings)
            },
            respondingEnabled: false,
            tags: [],
            translatedContent: {
                [lcid]: {
                    body: "",
                    description: "",
                    title: ""
                }
            },
            isSubmission: false,
            questions: {},
            actionButtons: defaultActionButtons,
        };
        return newEvent;
    }, [tenantSettings, notificationSettings, currentUser]);

    const validation = new Validation();
    const [event, setEvent] = useState<Event>({ ...newEvent });

    const [hasError, setHasError] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [validationChecks, setValidationChecks] = useState<ValidationChecks>(validation.getValidationChecks(event));
    const [isValid, setIsValid] = useState<boolean>(validation.isValid(validationChecks));
    const [showNotificationSettings, setShowNotificationSettings] = useState<boolean>(false);
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [showReminderSettings, setShowReminderSettings] = useState<boolean>(false);
    const [showRejectSubmissionDialog, setShowRejectSubmissionDialog] = useState<boolean>(false);
    const { customCss, fetching: isFetchingCustomCss } = useCustomTinyMceCssContent({ enabled: tenantSettings.showFeatures.tinyMceCustomCssEnabled, tenantId });
    const [showOneLastCheck, setShowOneLastCheck] = useState<boolean>(false);
    const [confirmDiscardEvent, setConfirmDiscardEvent] = useState<boolean>(false);

    const { openContentGuidelines, setOpenContentGuidelines } = useOpenContentGuidelines();

    const isContributor = getHighestRole(currentUser) === UserRoleStrings.CONTRIBUTOR;
    const draftId = match.params.draftId;
    const isDraftIdPresent = !!draftId && draftId !== "new";

    useDefaultLocale();

    useEffect(() => {
        const currValidation = new Validation();

        const onGetDraft = async (draftId: string) => {
            try {
                const draftEvent = await getDraft(draftId, ImageScale.Unprocessed);
                if (!!draftEvent) {
                    setEvent(draftEvent);
                    const checks = currValidation.getValidationChecks(draftEvent);
                    setValidationChecks(checks);
                    setIsValid(currValidation.isValid(checks));
                    setHasError(false);        
                }
                else {
                    setHasError(true);
                }
            }
            catch {
                setHasError(true);
            }
            finally {
                setIsFetching(false);
            }
        };

        if (draftId) {
            clearChangedSinceSaved();

            if (draftId === "new") {
                setEvent({ ...newEvent });

                const checks = currValidation.getValidationChecks(newEvent);
                setValidationChecks(checks);

                setHasError(false);
                setIsFetching(false);
                setIsValid(currValidation.isValid(checks));
            } else {
                onGetDraft(draftId);
            }
        }
    }, [draftId, newEvent, getDraft, clearChangedSinceSaved]);

    useEffect(() => {
        if (!!draftId) {
            if (isUpdatingUrl) {
                setEvent({ ...event, id: draftId });
                updateUrlComplete();
            }
        }
    }, [isUpdatingUrl, event, draftId, updateUrlComplete]);

    useEffect(() => {
        if (tagSettings.shouldFetch || !tagSettings.tagGroups.length)
            getTagSettings();
    }, [tagSettings.shouldFetch, tagSettings.tagGroups.length, getTagSettings]);

    const onHidePreview = () => setShowPreview(false);

    const redirectToManageEvents = useCallback(() => {
        redirectTo(`/${match.params.tenant}/admin/events`);
    }, [match.params.tenant, redirectTo]);

    const onPublishEvent = useCallback(async (sendNotification: boolean) => {
        onHidePreview();

        const succeeded = isContributor
            ? await submitEvent(draftId)
            : await publishEvent(draftId, sendNotification);

        if (succeeded) redirectToManageEvents();
    }, [isContributor, redirectToManageEvents, publishEvent, draftId, submitEvent]);

    const oneLastCheck = <OneLastCheck
        tags={event.tags?.map((tag) => tag.id)}
        open={showOneLastCheck}
        publishTime={event.publishTime ? new Date(event.publishTime) : new Date()}
        expiryTime={event.expiryTime ? new Date(event.expiryTime) : undefined}
        tenantSettings={tenantSettings}
        tenantNotificationSettings={notificationSettings}
        onClose={() => setShowOneLastCheck(false)}
        onPublish={async () => onPublishEvent(true)}
        notifications={event.notifications}
        commentsEnabledOnContent={event.commentingEnabled}
        isPreviouslyPublished={event.isPreviouslyPublished}
        disableKeepEditing={isPublishing}
        publishBtnName={getPublishBtnName(isContributor, event.isSubmission, isDraftIdPresent)}
        contentType={ContentType.Event}
    >
        <div className="event-preview">
            {event && <EventPreviewContent event={event}/>}
        </div>
    </OneLastCheck>;

    const eventPreview = React.useMemo(() => <EventPreview
            show={showPreview}
            event={event}
            onPublish={onPublishEvent}
            onClose={onHidePreview}
            isContributor={isContributor}
            inEditor
        />,
        [showPreview, event, onPublishEvent, isContributor]
    );

    const contentGuidelinePreview = React.useMemo(() => <ContentGuidelinePreview
        showPreview={openContentGuidelines}
        onClose={() => setOpenContentGuidelines(false)}
        isAuthoring
        hintType={HintType.Event}
    />, [openContentGuidelines]);

    const backToManageEvents = useCallback(async () => {
        if (!changedSinceSaved) return redirectToManageEvents();

        if (await confirm.show({
            title: "Back to Manage Events",
            yesColor: "#3b78ab",
            noColor: "#888888",
            text: (
                <React.Fragment>
                    <div className="emphasis">Your changes will not be saved.</div>
                    <br />
                    <div>Do you want to continue?</div>
                </React.Fragment>
            )
        })) {
            clearChangedSinceSaved();
            redirectToManageEvents();
        }
    }, [redirectToManageEvents, changedSinceSaved, clearChangedSinceSaved]);

    const discardEvent = useCallback(() => {
        if (changedSinceSaved) {
            setConfirmDiscardEvent(true);
            return;
        }

        clearChangedSinceSaved();
        redirectToManageEvents();
    }, [changedSinceSaved, clearChangedSinceSaved, redirectToManageEvents]);

    const onSave = useCallback(async (redirect: boolean = true) => {
        if (!event?.id) updateUrl();

        // a contributor moving a submission back to drafts
        const toSave = event?.isSubmission && isContributor ?  {...event, isSubmission: false} : event

        const response = toSave && await saveEvent(toSave)
        if (response?.id && redirect)
            redirectTo(`/${match.params.tenant}/admin/events/edit/${response.id}`);

        return response?.id;
    }, [match.params.tenant, redirectTo, saveEvent, event, updateUrl, isContributor])

    const onPublishEventCheck = useCallback(async () => {
        const draftId = await onSave();
        if (draftId) setShowOneLastCheck(true);
    }, [onSave]);

    const onPreviewEvent = useCallback(async () => {
        const draftId = await onSave();
        if (draftId) setShowPreview(true);
    }, [onSave]);

    const commands = React.useMemo(() => <EventAuthoringActionsAppBar
        actions={{
            discardEvent: discardEvent,
            publishEvent: onPublishEventCheck,
            saveEvent: onSave,
            openGuidelines: () => setOpenContentGuidelines(true),
            rejectSubmission: () => setShowRejectSubmissionDialog(true),
            previewEvent: onPreviewEvent
        }}
        appBarState={{
            hasDraft: Boolean(event.id),
            isValidDraft: isValid,
            isSubmission: event.isSubmission,
            isPublished: event.publishTime ? new Date(event.publishTime) < new Date() : false,
            isDraftIdPresent: isDraftIdPresent
        }}
    />, [isValid, event, isDraftIdPresent, discardEvent, onPublishEventCheck, onSave, onPreviewEvent]);

    const onChangeEvent = (value: Partial<Event>) => {
        const changedEvent = { ...event, ...value };
        setEvent(changedEvent);

        const checks = validation.getValidationChecks(changedEvent);
        setValidationChecks(checks);
        setIsValid(validation.isValid(checks));

        hasChangedSinceSaved();
    };

    const onRejectSubmission = async (comments?: string) => {
        try {
            await submissionsApi.unsubmitEvent(draftId, comments);
        }
        finally {
            clearChangedSinceSaved();
            redirectToManageEvents();
        }
    }
    
    const getRejectionDialog = (): JSX.Element =>
        <RejectSubmissionDialog
            unsavedChanges={changedSinceSaved}
            open={showRejectSubmissionDialog}
            setOpen={setShowRejectSubmissionDialog}
            saveProgress={async () => !!(await onSave(false)) }
            rejectSubmission={onRejectSubmission}
        />

    const getDiscardEventConfirm = (): JSX.Element => (
        <DiscardDraftConfirmDialog
            contentType={ContentType.Event}
            open={confirmDiscardEvent}
            onClose={() => setConfirmDiscardEvent(false)}
            onSaveChanges={async () => {
                setConfirmDiscardEvent(false);
                await onSave(false);
                redirectToManageEvents();
            }}
            onDiscard={() => {
                setConfirmDiscardEvent(false);
                clearChangedSinceSaved();
                redirectToManageEvents();
            }}
        />
    );

    return (
        <BasePage fullWidth>
            <Breadcrumb
                items={[
                    { title: "Events", onClick: backToManageEvents },
                    { title: draftId === "new" ? "Create new event" : "Edit event" }
                ]}
                backItem={{ title: "Back to Manage Events", onClick: backToManageEvents }}
                middleComponent={<DraftHistoryButton otherPopoutsClosed={!openContentGuidelines}/>}
                rightComponent={commands}
                isFlex
            />
            <MainContent>
                <div className="authoring-page">
                    {isFetching || !event || isFetchingCustomCss
                        ? <Loading />
                        : hasError
                            ? <div className="no-event-data">This event could not be loaded.</div>
                            : <PickerLocalization>
                                <EventEditor
                                    customCss={customCss}
                                    event={event}
                                    onChangeEvent={onChangeEvent}
                                    isValid={isValid}
                                    showNotificationSettings={showNotificationSettings}
                                    showReminderSettings={showReminderSettings}
                                    onShowNotificationSettings={setShowNotificationSettings}
                                    onShowReminderSettings={setShowReminderSettings}
                                    validationChecks={validationChecks}
                                />
                            </PickerLocalization>
                    }
                    {eventPreview}
                    {oneLastCheck}
                    {contentGuidelinePreview}
                    {getRejectionDialog()}
                    {getDiscardEventConfirm()}
                    <ErrorSnackbar
                        errorMessage={errorMessage}
                        clearErrorMessage={clearErrorMessage}
                    />
                    <LoadingOverlay absolute={true} show={isSaving} />
                </div>
            </MainContent>
        </BasePage>
    );
};

interface RouteParams {
    tenant: string;
    draftId: string;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: RouteComponentProps<RouteParams>) => ({
        ...ownProps,
        changedSinceSaved: state.events.changedSinceSaved,
        errorMessage: state.events.errorMessage,
        isSaving: state.events.isSaving,
        isUpdatingUrl: state.events.isUpdatingUrl,
        currentUser: state.settings.currentUser,
        notificationSettings: state.settings.notificationSettings,
        tagSettings: state.settings.tagSettings,
        tenantSettings: state.settings.tenantSettings,
        tenantId: state.tenant.id,
        guidelines: state.guidelines.guidelines,
        isGuidelineEmpty: state.guidelines.isEmpty,
        autoHideContentGuidelines: state.settings.currentUser.hideContentGuidelines,
        isPublishing: state.events.isPublishing
    }),
    {
        clearChangedSinceSaved: actions.clearChangedSinceSaved,
        hasChangedSinceSaved: actions.hasChangedSinceSaved,
        clearErrorMessage: actions.clearErrorMessage,
        getDraft: actions.getDraft,
        getTagSettings: settingsActions.getTagSettings,
        publishEvent: actions.publishEvent,
        submitEvent: actions.submitEvent,
        saveEvent: actions.saveEvent,
        updateUrl: actions.updateUrl,
        updateUrlComplete: actions.updateUrlComplete,
        redirectTo: push
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EventCreation);
